import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { AssessmentTemplate, RiskStatus, RiskType } from '@app/src/types/resourceExplorer'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { Close } from '@mui/icons-material'
import { Box, Dialog, IconButton, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import AssessmentModalTable from '../../ResourceCollection/Collections/DataHub/DataInsights/AssessmentsModalTable'

type AssessmentDialogProps = {
  assessmentTemplate: AssessmentTemplate
  activeLevel?: number
  periods?: string[]
  riskType?: RiskType
  riskStatus?: RiskStatus
  providerIds?: Array<number>
  isOpen: boolean
  closeModal: () => void
  countryName?: string
  filters?: FilterGroup[]
}

const AssessmentDialog: React.FC<AssessmentDialogProps> = ({
  assessmentTemplate,
  activeLevel,
  periods,
  riskType,
  riskStatus,
  providerIds,
  isOpen,
  closeModal,
  countryName,
  filters,
}) => {
  const { formatMessage } = useIntl()
  const isBaselineAssessment = assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment
  const levelNamesType = assessmentTemplate.levelNamesType ?? 'Intervals'

  const renderContent = () => {
    const levelLabel = !activeLevel
      ? undefined
      : isBaselineAssessment
        ? formatMessage({ id: 'baselineAssessment.totalLevel' }, { value: activeLevel })
        : assessmentTemplate.levels === 3
          ? formatMessage({ id: `assessments.${levelNamesType}.3.level${activeLevel}` })
          : formatMessage({ id: `assessments.${levelNamesType}.5.level${activeLevel}` })

    return (
      <>
        <Box px={4} py={1} bgcolor="grey.300">
          <Typography color="textSecondary">
            {[
              formatMessage({ id: 'assessments.breadcrumbsRootWord' }),
              assessmentTemplate.name,
              periods?.join(' + '),
              levelLabel,
              riskStatus ? formatMessage({ id: `schemas.risk.${riskStatus}` }) : undefined,
              countryName,
            ]
              .filter(Boolean)
              .join(' / ')}
          </Typography>
        </Box>

        <Stack flexGrow={1} height={10} pl={4} bgcolor="grey.100">
          <AssessmentModalTable
            assessmentTemplate={assessmentTemplate}
            activeLevel={activeLevel}
            periods={periods}
            riskType={riskType}
            riskStatus={riskStatus}
            providerIds={providerIds}
            filters={filters}
          />
        </Stack>
      </>
    )
  }

  return (
    <Dialog open={isOpen} onClose={closeModal} fullScreen sx={{ m: 10 }} PaperProps={{ sx: { borderRadius: 2 } }}>
      <Box height="100%" margin={0} display="flex" flexDirection="column">
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" py={2} px={4}>
          <Stack>
            <Typography variant="h6">{formatMessage({ id: 'assessments.title' })}</Typography>
          </Stack>

          <IconButton onClick={closeModal}>
            <Close />
          </IconButton>
        </Box>

        {isOpen && renderContent()}
      </Box>
    </Dialog>
  )
}

export default AssessmentDialog
